import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import {
  X, RotateCw,
  FileText, File, Image as ImageIcon, FileSpreadsheet, FileType
} from 'lucide-react';
import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import ThreeDotBouncingLoader from "../../../../common/element/ButtonLoader/ThreeDotBouncingLoder";

if (typeof window !== 'undefined') {
  Modal.setAppElement('#root');
}

const DocumentViewerModal = ({ isOpen, setIsOpen, documentData = {} }) => {
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [iframeKey, setIframeKey] = useState(0);
  const retryTimerRef = useRef(null);

  // Track loaded docs to keep them in memory (instant switching)
  const [loadedIndices, setLoadedIndices] = useState(new Set([0]));

  useEffect(() => {
    if (isOpen) {
      setActiveDocIndex(0);
      setZoom(100);
      setRotation(0);
      setLoadedIndices(new Set([0]));
      setIframeKey(0);
    }
  }, [isOpen, documentData]);

  useEffect(() => {
    setLoadedIndices(prev => {
      const newSet = new Set(prev);
      newSet.add(activeDocIndex);
      return newSet;
    });
    setZoom(100);
    setRotation(0);
    setIsIframeLoading(true);
    setIframeKey(k => k + 1);
  }, [activeDocIndex]);

  // Auto-retry DOCX iframe if it hasn't loaded within 8 seconds
  useEffect(() => {
    const currentDoc = documentsList[activeDocIndex];
    const type = currentDoc?.file_type?.toLowerCase() || '';
    const isOffice = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(type);

    if (isOffice && isIframeLoading) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(() => {
        setIframeKey(k => k + 1);
        setIsIframeLoading(true);
      }, 8000);
    }
    return () => clearTimeout(retryTimerRef.current);
  }, [iframeKey, isIframeLoading, activeDocIndex]);

  useEffect(() => {
    if (isOpen) {
      const handleContextMenu = (e) => e.preventDefault();
      const handleKeyDown = (e) => {
        if (
          e.key === 'PrintScreen' ||
          ((e.ctrlKey || e.metaKey) && ['p', 's', 'c'].includes(e.key.toLowerCase()))
        ) {
          e.preventDefault();
        }
      };

      document.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen]);

  // --- DATA ---
  const documentsList = documentData?.supportedDocument?.documents || [];
  const docName = documentData?.supportedDocument?.name || 'Unknown Document';
  const sessionName = documentData?.companySession?.session_name || 'N/A';
  const createdAtRaw = documentData?.created_at;

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const getFileIcon = (type) => {
    if (['doc', 'docx'].includes(type)) return <FileText className="w-4 h-4 text-blue-600" />;
    if (['xls', 'xlsx'].includes(type)) return <FileSpreadsheet className="w-4 h-4 text-green-600" />;
    if (['pdf'].includes(type)) return <FileType className="w-4 h-4 text-red-600" />;
    if (['jpg', 'png', 'jpeg', 'webp'].includes(type)) return <ImageIcon className="w-4 h-4 text-purple-600" />;
    return <File className="w-4 h-4 text-gray-500" />;
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 10, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 10, 50));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleClose = () => setIsOpen(false);

  const customStyles = {
    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000 },
    content: { top: 0, left: 0, right: 0, bottom: 0, padding: 0, border: 'none', background: 'white' },
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose} style={customStyles}>
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .pdf-viewer-wrapper [class*="rpv-core__"] {
          background-color: transparent !important;
        }
      `}</style>

      <div className="flex h-screen select-none">

        {/* LEFT SIDEBAR */}
        <div className="w-72 bg-[#FFF4E5] border-r border-gray-200 p-6 flex-shrink-0 flex flex-col overflow-y-auto">
          <div className="space-y-4 mb-6">
            <h3 className="text-base font-semibold text-gray-900 break-words">{docName}</h3>
            <p className="text-md text-gray-600">{formatDate(createdAtRaw)}</p>
            <div>
              <p className="text-md text-gray-900 font-medium mb-1">Session Name</p>
              <p className="text-md text-gray-600">{sessionName}</p>
            </div>
          </div>

          {documentsList.length > 0 && (
            <div className="mt-2">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">
                Documents ({documentsList.length})
              </h4>
              <div className="flex flex-col gap-2">
                {documentsList.map((doc, index) => {
                  const isActive = activeDocIndex === index;
                  const type = doc.file_type?.toLowerCase();
                  return (
                    <button
                      key={index}
                      onClick={() => setActiveDocIndex(index)}
                      className={`
                        flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-all duration-200 border text-left
                        ${isActive
                          ? 'bg-white border-orange-300 shadow-md text-gray-900 font-medium ring-1 ring-orange-200'
                          : 'bg-transparent border-transparent hover:bg-orange-100 text-gray-600 hover:text-gray-900'}
                      `}
                    >
                      <div className={`p-1.5 rounded ${isActive ? 'bg-orange-50' : 'bg-gray-100'}`}>
                        {getFileIcon(type)}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">Document {index + 1}</span>
                        <span className="text-[10px] text-gray-400 uppercase">{type} • {doc.document_size || 'N/A'}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* MAIN CONTENT */}
        <div className="flex-1 flex flex-col relative bg-white">

          {/* HEADER TOOLBAR */}
          <div className="h-14 bg-[#FFF4E5] border-b border-gray-200 flex items-center justify-between px-6 z-20 relative">
            <div className="flex-1"></div>

            <div className="flex-1 flex items-center justify-center">
              {documentsList.length > 0 && (() => {
                const activeType = documentsList[activeDocIndex]?.file_type?.toLowerCase() || '';
                const isOfficeActive = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(activeType);
                return !isOfficeActive ? (
                  <div className="flex items-center bg-white rounded-md border border-gray-200 overflow-hidden shadow-sm">
                    <button onClick={handleZoomOut} className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-50">-</button>
                    <div className="px-3 text-xs font-medium border-x border-gray-200 h-8 flex items-center justify-center min-w-[54px]">{zoom}%</div>
                    <button onClick={handleZoomIn} className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-50">+</button>
                  </div>
                ) : null;
              })()}
            </div>

            <div className="flex-1 flex items-center justify-end gap-1">
              <button title="Rotate" onClick={handleRotate} className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 rounded"> <RotateCw size={16} /> </button>
              <div className="w-px h-5 bg-gray-300 mx-1"></div>
              <button title="Close" onClick={handleClose} className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 rounded"> <X size={20} /> </button>
            </div>
          </div>

          {/* VIEWPORT AREA */}
          <div className="flex-1 relative overflow-hidden flex items-center justify-center bg-gray-400">

            {documentsList.map((doc, index) => {
              if (!loadedIndices.has(index)) return null;

              const isActive = activeDocIndex === index;
              const type = doc.file_type?.toLowerCase() || '';
              const isImage = ['jpg', 'png', 'jpeg', 'gif', 'webp'].includes(type);
              const isOffice = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(type);
              const isPdf = type === 'pdf';

              let docUrl = doc.document_url?.trim();
              if (isImage && docUrl && !docUrl.includes('?')) {
                docUrl = `${docUrl}?t=${new Date().getTime()}`;
              }

              return (
                <div
                  key={index}
                  className={`absolute inset-0 w-full h-full flex items-center justify-center bg-gray-400 ${isActive ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
                  style={{ display: isActive ? 'flex' : 'none' }}
                >
                  <div
                    className={`transition-all duration-200 flex items-center justify-center ${isImage ? 'bg-white shadow-lg' : 'w-full h-full'}`}
                    style={isImage ? {
                      width: zoom === 100 ? 'auto' : `calc(${zoom}% * 0.8)`,
                      height: zoom === 100 ? '90%' : 'auto',
                      maxWidth: '90%',
                      maxHeight: '90%',
                      transform: `rotate(${rotation}deg)`,
                      transformOrigin: 'center center',
                    } : {
                      transform: `rotate(${rotation}deg)`,
                      transformOrigin: 'center center',
                    }}
                  >

                    {/* 1. IMAGE */}
                    {isImage && (
                      <img
                        src={docUrl}
                        alt="Document"
                        className="w-full h-full object-contain block"
                        referrerPolicy="no-referrer"
                        crossOrigin="anonymous"
                      />
                    )}

                    {/* 2. GOOGLE VIEWER for Office files */}
                    {isOffice && (
                      <div className="w-full h-full relative bg-gray-400">
                        {isIframeLoading && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center z-20 gap-3">
                            <ThreeDotBouncingLoader />
                            <p className="text-sm text-white/80">Loading document...</p>
                          </div>
                        )}
                        <iframe
                          key={iframeKey}
                          src={`https://docs.google.com/gview?url=${encodeURIComponent(doc.document_url)}&embedded=true`}
                          className={`w-full h-full border-0 transition-opacity duration-300 ${isIframeLoading ? 'opacity-0' : 'opacity-100'}`}
                          title={`Office Viewer ${index}`}
                          onLoad={() => {
                            clearTimeout(retryTimerRef.current);
                            setIsIframeLoading(false);
                          }}
                        />
                        {/* Cover Google Docs viewer's dark left panel */}
                        <div className="absolute top-0 left-0 h-full bg-gray-400 z-10" style={{ width: '23%' }} />
                        {/* Cover Google Docs viewer's dark right panel + pop-out button */}
                        <div className="absolute top-0 right-0 h-full bg-gray-400 z-10" style={{ width: '23%' }} />
                      </div>
                    )}

                    {/* 3. PDF VIEWER */}
                    {isPdf && doc.document_url && (
                      <div className="w-full h-full overflow-y-auto flex justify-center bg-gray-400 pdf-viewer-wrapper">
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                          <Viewer
                            key={zoom}
                            fileUrl={doc.document_url}
                            defaultScale={zoom / 100}
                            theme="light"
                          />
                        </Worker>
                      </div>
                    )}

                    {!isImage && !isOffice && !isPdf && (
                      <div className="text-center p-6">
                        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Preview not available for .{type}</p>
                      </div>
                    )}

                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </Modal>
  );
};

export default DocumentViewerModal;
